/**
 * Google Gemini Provider
 *
 * Cloud LLM provider using Google Gemini API (FREE tier available)
 * Default: gemini-2.0-flash-exp model
 */

import { LLMProvider, GenerateOptions } from './base';

import { pluginLogger } from '@/lib/platform/utils/logger';

export class GeminiProvider implements LLMProvider {
  name = 'Google Gemini';

  constructor(
    private apiKey: string,
    private model: string = 'gemini-2.0-flash-exp'
  ) {}

  async generateInsights(prompt: string, options?: GenerateOptions): Promise<string> {
    pluginLogger.log(`[Gemini] Using model: ${this.model}`);
    const timeout = options?.timeout || 30000; // 30 seconds default
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
      const response = await fetch(
        `https://generativelanguage.googleapis.com/v1beta/models/${this.model}:generateContent?key=${this.apiKey}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            contents: [
              {
                parts: [
                  {
                    text: `You are a sports business analyst that provides insights in JSON format.\n\n${prompt}`,
                  },
                ],
              },
            ],
            generationConfig: {
              temperature: options?.temperature || 0.7,
              maxOutputTokens: options?.maxTokens || 1000,
            },
          }),
          signal: controller.signal,
        }
      );

      clearTimeout(timeoutId);

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        const errorMessage = error.error?.message || response.statusText;
        const errorStatus = error.error?.status || '';

        pluginLogger.error(`[Gemini] API error for model ${this.model}:`, error);

        // Detect rate limit and quota errors
        if (
          response.status === 429 ||
          errorMessage.toLowerCase().includes('quota') ||
          errorMessage.toLowerCase().includes('rate limit') ||
          errorStatus === 'RESOURCE_EXHAUSTED'
        ) {
          throw new Error(`RATE_LIMIT: ${errorMessage}`);
        }

        throw new Error(`Gemini API error: ${errorMessage}`);
      }

      const data = await response.json();

      // Log response for debugging
      pluginLogger.log(
        `[Gemini] API Response candidates:`,
        JSON.stringify(data.candidates, null, 2)
      );

      // Validate response structure
      if (!data.candidates || data.candidates.length === 0) {
        pluginLogger.error(`[Gemini] No candidates in response:`, data);
        throw new Error('No candidates in Gemini API response');
      }

      const candidate = data.candidates[0];

      // Check for MAX_TOKENS finish reason
      if (candidate.finishReason === 'MAX_TOKENS') {
        pluginLogger.error(`[Gemini] Response truncated due to MAX_TOKENS`);
        throw new Error(
          'MAX_TOKENS: Gemini response was truncated. The prompt or response is too long. Try selecting fewer valuations or reducing the custom prompt.'
        );
      }

      if (!candidate.content || !candidate.content.parts || candidate.content.parts.length === 0) {
        pluginLogger.error(`[Gemini] Invalid content structure:`, candidate);
        throw new Error('Invalid content structure from Gemini API');
      }

      if (!candidate.content.parts[0].text) {
        pluginLogger.error(`[Gemini] No text in first part:`, candidate.content.parts[0]);
        throw new Error('No text in Gemini API response');
      }

      const text = data.candidates[0].content.parts[0].text;
      pluginLogger.log(`[Gemini] Generated text:`, text.substring(0, 200) + '...');

      return text;
    } catch (error) {
      clearTimeout(timeoutId);

      if (error instanceof Error && error.name === 'AbortError') {
        throw new Error(`Gemini request timed out after ${timeout}ms`);
      }

      // Preserve rate limit errors
      if (error instanceof Error && error.message?.startsWith('RATE_LIMIT:')) {
        throw error;
      }

      throw new Error(`Gemini error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  async isAvailable(): Promise<boolean> {
    if (!this.apiKey) {
      return false;
    }

    try {
      // Test with a simple request
      const response = await fetch(
        `https://generativelanguage.googleapis.com/v1beta/models?key=${this.apiKey}`,
        {
          method: 'GET',
          signal: AbortSignal.timeout(3000),
        }
      );

      return response.ok;
    } catch {
      return false;
    }
  }
}
